import React, { useState } from 'react';
import { Search, Plus, X, Monitor, Barcode, ShoppingBag } from 'lucide-react';
import POSReceipt from './POSReceipt';

const POSView1 = ({
  searchTerm,
  setSearchTerm,
  handleKeyDown,
  handleSearchFocus,
  isSearching,
  searchResults,
  selectedIndex,
  handleProductSelect,
  isQuantityFocused,
  isUomFocused,
  quantity,
  setQuantity,
  setIsQuantityFocused,
  setIsUomFocused,
  uomValue,
  setUomValue,
  selectedProduct,
  cartItems,
  setCartItems,
  paymentMethod,
  setPaymentMethod,
  paidAmount,
  setPaidAmount,
  processSale,
  isProcessingSale,
  handleHoldSales,
  heldSales,
  setShowHeldSalesModal,
  handleCancel,
  formatCurrency,
  currentTime,
  transactionCount,
  user,
  systemStatus,
  showConfirmation,
  handleReturnSale,
  addToCart
}) => {
  // Receipt state
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  // Handle successful sale and show receipt
  const handleProcessSaleWithReceipt = async () => {
    // Store current cart data before processing sale
    const currentCartItems = [...cartItems];
    const currentPaidAmount = paidAmount;
    const currentPaymentMethod = paymentMethod;
    
    try {
      // Call the original processSale function
      await processSale();
      
      // Prepare receipt data with the stored cart data
      const totalAmount = currentCartItems.reduce((total, item) => total + (item.amount || 0), 0);
      const changeAmount = Math.max(0, currentPaidAmount - totalAmount);
      
      const receipt = {
        reference_number: `POS-${Date.now()}`,
        date: currentTime.toLocaleDateString('en-GB'),
        time: currentTime.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        }),
        cashier_name: user?.name || user?.username || 'Unknown',
        payment_method: currentPaymentMethod,
        company_name: 'Grovalue Enterprise',
        company_address: '123 Business Street, City, Country',
        company_phone: '+233 XX XXX XXXX',
        items: currentCartItems.map(item => ({
          name: item.product.name,
          quantity: item.quantity,
          uom: item.uom,
          price: item.price,
          amount: item.amount
        })),
        subtotal: totalAmount,
        discount: 0,
        vat: 0,
        vat_rate: 0,
        total_amount: totalAmount,
        amount_paid: currentPaidAmount,
        change_amount: changeAmount
      };
      
      setReceiptData(receipt);
      setShowReceipt(true);
    } catch (error) {
      console.error('Error processing sale:', error);
    }
  };

  // Handle receipt actions
  const handleReceiptPrint = (receiptElement) => {
    if (receiptElement) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>POS Receipt - ${receiptData.reference_number}</title>
            <style>
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                margin: 0; 
                padding: 20px;
                background: white;
              }
              .receipt { 
                max-width: 300px; 
                margin: 0 auto; 
                border: 1px solid #ccc; 
                padding: 20px;
              }
              @media print {
                body { margin: 0; padding: 0; }
                .receipt { border: none; padding: 10px; }
              }
            </style>
          </head>
          <body>
            <div class="receipt">${receiptElement.innerHTML}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleReceiptDownload = (receiptElement) => {
    if (receiptElement) {
      const content = receiptElement.innerText;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${receiptData.reference_number}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div 
      className="flex flex-col relative" 
      style={{
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Top Header Bar */}
      <div className="bg-blue-500 text-white p-3 flex items-center justify-between relative z-30">
        <div className="flex items-center space-x-8">
          <h1 className="text-lg font-bold">Point of Sales</h1>
          <div className="flex items-center space-x-6 text-sm">
            <span>PCS: {formatCurrency(selectedProduct ? (parseFloat(selectedProduct.selling_price_pcs) || 0) : 0)}</span>
            <span>CTN: {formatCurrency(selectedProduct ? (parseFloat(selectedProduct.selling_price_ctn) || 0) : 0)}</span>
            <span>{currentTime.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} {currentTime.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            })}</span>
          </div>
        </div>
        <div className="flex items-center space-x-6">
          <span>Transactions : {transactionCount}</span>
          <span>Cashier : {user?.name || user?.username || 'Unknown'}</span>
          
          <button 
            onClick={() => {
              console.log('Return Sale button clicked');
              handleReturnSale();
            }}
            className="bg-green-500 hover:bg-green-600 px-3 py-1 rounded text-sm font-medium transition-colors relative z-20"
          >
            Return Sale
          </button>
          <button 
            onClick={() => {
              console.log('Exit POS button clicked');
              showConfirmation(
                'exit-pos',
                null,
                'Exit POS',
                'Are you sure you want to exit the POS system? Any unsaved changes will be lost.'
              );
            }}
            className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded text-sm font-medium transition-colors relative z-20"
          >
            Exit POS
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex min-h-0 overflow-hidden flex-1">
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-black/20 pointer-events-none"></div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col relative z-10">
          {/* Product Search and Input Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-3 shadow-lg">
            <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-2 lg:space-y-0 lg:space-x-6">
              <div className="w-full lg:w-1/2 flex items-center space-x-2 bg-white rounded px-3 h-10 py-1 relative search-container">
                <Search size={16} className="text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search products by Name and Productl" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                  onKeyDown={handleKeyDown}
                  onFocus={handleSearchFocus}
                  className="flex-1 outline-none text-gray-700 text-sm uppercase"
                />
                {isSearching && (
                  <div className="absolute right-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
                
                {/* Search Results Dropdown */}
                {searchTerm && searchTerm.length >= 2 && !isQuantityFocused && !isUomFocused && (
                  <div className="search-results-dropdown absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto mt-1">
                    {searchResults.length > 0 ? (
                      searchResults.map((product, index) => (
                        <div
                          key={product.id}
                          data-index={index}
                          onClick={() => handleProductSelect(product)}
                          className={`px-3 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                            index === selectedIndex 
                              ? (selectedIndex % 2 === 0 ? 'bg-red-100 border-red-300' : 'bg-blue-100 border-blue-300')
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-base">{product.name}</div>
                            </div>
                            <div className="flex items-center space-x-3 ml-4">
                              <div className="text-base font-semibold text-blue-600 min-w-[60px] text-right">
                                {product.selling_price_ctn && formatCurrency(product.selling_price_ctn)}
                              </div>
                              <div className="text-base font-semibold text-green-600 min-w-[60px] text-right">
                                {product.selling_price_pcs && formatCurrency(product.selling_price_pcs)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-center text-gray-500">
                        No products found for "{searchTerm}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-white font-bold text-sm">Qty: </label>
                <input 
                  id="quantity-input"
                  type="number" 
                  step="0.01"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
                  onFocus={(e) => {
                    setIsQuantityFocused(true);
                    e.target.select(); // Highlight the text
                  }}
                  onBlur={() => setIsQuantityFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      console.log('Enter pressed in quantity field');
                      const uomSelect = document.getElementById('uom-select');
                      console.log('UoM select element:', uomSelect);
                      if (uomSelect) {
                        // Focus the element and add visual feedback
                        uomSelect.focus();
                        console.log('Focus set on UoM select');
                        
                        // Add visual feedback to show it's selected
                        uomSelect.style.border = '3px solid #ef4444';
                        uomSelect.style.backgroundColor = '#fef2f2';
                        uomSelect.style.boxShadow = '0 0 10px rgba(239, 68, 68, 0.5)';
                        
                        // Test if focus actually worked
                        setTimeout(() => {
                          console.log('Active element:', document.activeElement);
                          console.log('UoM element focused:', document.activeElement === uomSelect);
                        }, 50);
                        
                        setTimeout(() => {
                          uomSelect.style.border = '';
                          uomSelect.style.backgroundColor = '';
                          uomSelect.style.boxShadow = '';
                        }, 2000);
                      } else {
                        console.log('UoM select element not found');
                      }
                    }
                  }}
                  className="w-18 lg:w-14 px-2 py-1 rounded border-none outline-none text-sm h-8"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-white font-bold text-sm">UoM: </label>
                <select 
                  id="uom-select"
                  tabIndex="0"
                  value={uomValue}
                  onChange={(e) => setUomValue(e.target.value)}
                  onFocus={(e) => {
                    // For select elements, we can't use select() but we can ensure focus
                    e.target.focus();
                    setIsUomFocused(true);
                    console.log('UoM field focused');
                    
                    // Add visual feedback when focused
                    e.target.style.border = '2px solid #3b82f6';
                    e.target.style.backgroundColor = '#dbeafe';
                  }}
                  onBlur={(e) => {
                    // Remove visual feedback when focus is lost
                    setIsUomFocused(false);
                    e.target.style.border = '';
                    e.target.style.backgroundColor = '';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addToCart();
                    }
                    if (e.key === ' ') {
                      e.preventDefault();
                      console.log('Space pressed in UoM field');
                    }
                  }}
                  className="w-20 lg:w-auto px-2 py-1 rounded border-none outline-none text-sm h-8"
                >
                  <option>Ctn</option>
                  <option>Pcs</option>
                </select>
              </div>
              <div className="text-white font-bold text-sm">
                Current Qty (CTN) : {selectedProduct ? (selectedProduct.quantity || 0).toFixed(2) : 0}
              </div>
            </div>
          </div>

          {/* Product List Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-2 shadow-lg">
            <div className="flex items-center font-bold text-sm">
              <div className="w-2/6 lg:w-2/6 px-2 lg:px-4 truncate">PRODUCT</div>
              <div className="w-1/6 lg:w-3/12 px-1 lg:px-3 text-center truncate">QTY</div>
              <div className="w-1/6 lg:w-1/12 px-1 lg:px-3 text-center truncate">UOM</div>
              <div className="w-1/6 lg:w-1/12 px-1 lg:px-3 text-center truncate">PRICE</div>
              <div className="w-1/6 lg:w-2/12 px-1 lg:px-3 text-center truncate">AMOUNT</div>
              <div className="w-1/6 lg:w-1/12 px-1 lg:px-3 text-center truncate">ACTION</div>
            </div>
          </div>

          {/* Cart Area */}
          <div className="bg-gradient-to-br from-white to-gray-50 overflow-y-auto min-h-0 flex-1 shadow-inner">
            {cartItems.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-[672px] h-[672px] bg-gradient-to-br from-red-500/10 to-blue-600/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-10 mb-12">
                        <Monitor size={72} className="text-gray-400/60" />
                        <Barcode size={72} className="text-gray-400/60" />
                        <ShoppingBag size={72} className="text-gray-400/60" />
                      </div>
                      <div className="text-gray-500/70 font-bold text-9xl mb-8">NEXPOS</div>
                      <div className="text-gray-500/70 text-6xl font-semibold">SYSTEM</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4">
                <div className="space-y-1">
                  {cartItems.map((item, index) => (
                    <div key={item.id} className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
                      <div className="flex items-center justify-between text-sm">
                        <div className="w-2/6 lg:w-2/6 px-2 lg:px-4 truncate font-medium">
                          {item.product.name}
                        </div>
                        <div className="w-1/6 lg:w-3/12 px-1 lg:px-3 text-center truncate">
                          {item.quantity}
                        </div>
                        <div className="w-1/6 lg:w-1/12 px-1 lg:px-3 text-center truncate">
                          {item.uom}
                        </div>
                        <div className="w-1/6 lg:w-1/12 px-1 lg:px-3 text-center truncate">
                          {formatCurrency(item.price || 0)}
                        </div>
                        <div className="w-1/6 lg:w-2/12 px-1 lg:px-3 text-center truncate font-semibold">
                          {formatCurrency(item.amount || 0)}
                        </div>
                        <div className="w-1/6 lg:w-1/12 px-1 lg:px-3 text-center truncate">
                          <div className="flex items-center justify-center space-x-1">
                            <button 
                              onClick={() => {
                                setCartItems(prev => prev.map(cartItem => 
                                  cartItem.id === item.id 
                                    ? { ...cartItem, quantity: Math.max(1, cartItem.quantity - 1), amount: (Math.max(1, cartItem.quantity - 1) * cartItem.price) }
                                    : cartItem
                                ));
                              }}
                              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold transition-colors"
                            >
                              -
                            </button>
                            <button 
                              onClick={() => {
                                setCartItems(prev => prev.map(cartItem => 
                                  cartItem.id === item.id 
                                    ? { ...cartItem, quantity: cartItem.quantity + 1, amount: ((cartItem.quantity + 1) * cartItem.price) }
                                    : cartItem
                                ));
                              }}
                              className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold transition-colors"
                            >
                              +
                            </button>
                            <button 
                              onClick={() => {
                                showConfirmation(
                                  'delete-cart-item',
                                  item.id,
                                  'Remove Item',
                                  `Are you sure you want to remove "${item.product.name}" from the cart?`
                                );
                              }}
                              className="w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Payment and Actions */}
        <div className="w-full lg:w-80 bg-gray-100 flex flex-col border-l-0 lg:border-l-2 border-gray-300 min-h-0 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
          {/* Payment Details */}
          <div className="p-4 space-y-4">
            <div className="bg-black text-white p-4 rounded-lg shadow-lg relative">
              <div className="font-bold text-lg mb-3 text-center">Payment Method</div>
              <select
                id="payment-method-select"
                value={paymentMethod}
                onChange={(e) => {
                  console.log('Payment method changed to:', e.target.value);
                  setPaymentMethod(e.target.value);
                }}
                onClick={() => console.log('Payment dropdown clicked')}
                className="w-full bg-blue-500 hover:bg-blue-600 py-3 rounded-lg font-bold text-lg text-white text-center cursor-pointer"
              >
                <option value="Cash">Cash</option>
                <option value="Mobile Money">Mobile Money</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Visa">Visa</option>
              </select>
            </div>

            <div className="bg-black text-white p-4 rounded-lg shadow-lg">
              <div className="font-bold text-lg mb-3 text-center">Paid Amount</div>
              <input
                id="paid-amount-input"
                type="number"
                value={paidAmount}
                onChange={(e) => setPaidAmount(parseFloat((parseFloat(e.target.value) || 0).toFixed(2)))}
                className="bg-blue-500 px-4 py-3 rounded-lg font-bold text-2xl text-center shadow-md w-full border-none outline-none text-white placeholder-white/70"
                placeholder="0.00"
                step="0.01"
                min="0"
                onKeyDown={(e) => {
                  if (e.key === ' ') {
                    e.preventDefault();
                    processSale();
                  }
                }}
              />
            </div>

            <div className="bg-red-600 text-white p-4 rounded-lg shadow-lg">
              <div className="font-bold text-lg mb-3 text-center">Change</div>
              <div className="font-bold text-2xl text-center">
                {formatCurrency(Math.max(0, paidAmount - cartItems.reduce((total, item) => total + (item.amount || 0), 0)))}
              </div>
            </div>
          </div>

          {/* Total Amount - Moved to top for visibility */}
          <div className="p-2 flex-shrink-0">
            <div className="bg-gradient-to-r from-black to-gray-800 text-white p-3 rounded-lg shadow-xl border-2 border-yellow-400">
              <div className="font-bold text-lg mb-2 text-center text-yellow-300">TOTAL AMOUNT</div>
              <div className="text-yellow-400 font-bold text-3xl text-center">
                {formatCurrency(cartItems.reduce((total, item) => total + (item.amount || 0), 0))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-2 pb-9 space-y-2 flex-1 flex flex-col justify-end">
            <button 
              onClick={handleProcessSaleWithReceipt}
              disabled={isProcessingSale || cartItems.length === 0}
              className={`w-full py-3 rounded-lg font-bold text-lg transition-colors shadow-lg transform hover:scale-105 ${
                isProcessingSale || cartItems.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isProcessingSale ? 'Processing...' : 'Process Sales'}
            </button>
            
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={handleHoldSales}
                disabled={cartItems.length === 0}
                className={`py-2 rounded-lg font-bold text-base transition-colors shadow-md transform hover:scale-105 ${
                  cartItems.length === 0
                    ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                Hold Sales
              </button>
              <button 
                onClick={() => setShowHeldSalesModal(true)}
                disabled={heldSales.length === 0}
                className={`py-2 rounded-lg font-bold text-base transition-colors shadow-md transform hover:scale-105 ${
                  heldSales.length === 0
                    ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                Open Held Sales ({heldSales.length})
              </button>
            </div>
            
            <button 
              onClick={handleCancel}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-bold text-lg transition-colors shadow-lg transform hover:scale-105"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceipt && (
        <POSReceipt
          receiptData={receiptData}
          onClose={() => setShowReceipt(false)}
          onPrint={handleReceiptPrint}
          onDownload={handleReceiptDownload}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

export default POSView1;